import { useCallback, useRef, useState } from 'react';

/**
 * Manages controlled/uncontrolled state for a component.
 *
 * When `value` is provided, the component is controlled — state is owned externally.
 * When `value` is undefined, the component manages its own state using `defaultValue`.
 * In both modes, `onChange` is called when the value changes.
 */
export function useControlledState<T>(
  value: T | undefined,
  defaultValue: T,
  onChange?: (newValue: T) => void
): [T, (newValue: T) => void] {
  const isControlled = value !== undefined;
  const isControlledRef = useRef(isControlled);

  if (process.env['NODE_ENV'] !== 'production') {
    if (isControlledRef.current !== isControlled) {
      console.warn(
        'A component is changing from %s to %s. This is not supported.',
        isControlledRef.current ? 'controlled' : 'uncontrolled',
        isControlled ? 'controlled' : 'uncontrolled'
      );
      isControlledRef.current = isControlled;
    }
  }

  const [internalValue, setInternalValue] = useState(defaultValue);

  const currentValue = isControlled ? value : internalValue;

  const setValue = useCallback(
    (newValue: T) => {
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    },
    [isControlled, onChange]
  );

  return [currentValue, setValue];
}
